import { Injectable } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';

export interface CachedThumb {
  thumbsrc: string; // blob URL
  imgsrc: SafeUrl; // sanitized blob URL bound to [src]
}

const MAX_ENTRIES = 2000;

/**
 * In-memory LRU cache of decrypted thumbnails keyed by `cachekey`, surviving folder navigation
 * (cleared on logout) and deduping in-flight fetches so revisits/scroll-back render with no network.
 */
@Injectable({
  providedIn: 'root',
})
export class ThumbnailCacheService {
  // Map insertion order gives LRU-by-access (re-inserting a key moves it to the end).
  private readonly store = new Map<string, CachedThumb>();
  private readonly pending = new Set<string>();
  // Every blob URL ever issued (incl. evicted) so clear() can revoke them all (see set()).
  private readonly issuedBlobUrls = new Set<string>();

  public get(cachekey: string): CachedThumb | undefined {
    if (!cachekey) {
      return undefined;
    }
    const entry = this.store.get(cachekey);
    if (entry) {
      // Re-insert to mark most-recently-used.
      this.store.delete(cachekey);
      this.store.set(cachekey, entry);
    }
    return entry;
  }

  public set(cachekey: string, thumb: CachedThumb): void {
    if (!cachekey) {
      return;
    }
    this.store.delete(cachekey);
    this.store.set(cachekey, thumb);
    if (thumb.thumbsrc) {
      this.issuedBlobUrls.add(thumb.thumbsrc);
    }
    if (this.store.size > MAX_ENTRIES) {
      const oldestKey = this.store.keys().next().value as string;
      this.store.delete(oldestKey);
      // Not revoking the evicted blob URL here: the store/rendered rows may still display it
      // (revoking a referenced blob URL shows a broken image). clear() releases them all on logout.
    }
  }

  public isPending(cachekey: string): boolean {
    return this.pending.has(cachekey);
  }

  public markPending(cachekeys: string[]): void {
    cachekeys.forEach((key) => key && this.pending.add(key));
  }

  public unmarkPending(cachekeys: string[]): void {
    cachekeys.forEach((key) => this.pending.delete(key));
  }

  public clear(): void {
    // Safe to revoke all blob URLs now — the store is resetting (logout), nothing references them.
    this.issuedBlobUrls.forEach((url) => url && URL.revokeObjectURL(url));
    this.issuedBlobUrls.clear();
    this.store.clear();
    this.pending.clear();
  }
}
